import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Upload, Copy, Download, Trash2, HelpCircle, PlayCircle, StopCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { authService } from '../services/authService';
import { saveTask, removeTask, getTasksForComponent } from '../services/taskPersistence';
import { logUiEvent } from '../services/uiLogger';
import { transcribeService } from '../services/transcribeService';
import { buildDownloadName } from '../utils/downloadName';
import { resolveQuotaBlockedError } from '../lib/quotaError';
import { ENDPOINTS } from '../config/endpoints';
import { stereoRoleLabel } from '../lib/recognition/sources/stereo';

const API_BASE_URL_DUAL = ENDPOINTS.dual;

// Маппинг v3-формата (start/end, is_backchannel, nested words) на плоский
// формат, который рендерит UI (start_time/end_time). Поддерживает и legacy-вход:
// если приходят start_time/end_time без is_backchannel — нормализатор no-op.
// Backchannels («ага», «угу» < 0.8с) скрываются из диалога.
const normalizeSegments = (rawSegments) => {
  if (!Array.isArray(rawSegments)) return [];
  return rawSegments
    .filter((s) => !s.is_backchannel)
    .map((s) => ({
      speaker: s.speaker,
      speaker_role: s.speaker_role,
      text: s.text,
      start_time: s.start_time ?? s.start,
      end_time: s.end_time ?? s.end,
      words: s.words || [],
    }));
};

const DualChannelTranscription = ({ state, setState }) => {
  const [file, setFile] = useState(state.file || null);
  const [segments, setSegments] = useState(state.segments || []);
  const [taskId, setTaskId] = useState(state.taskId || null);
  const [isProcessing, setIsProcessing] = useState(state.isProcessing || false);
  const [progress, setProgress] = useState(state.progress || 0);
  const [uploadProgress, setUploadProgress] = useState(state.uploadProgress || 0);
  const [transcribeProgress, setTranscribeProgress] = useState(state.transcribeProgress || 0);
  const [currentProcessType, setCurrentProcessType] = useState(state.currentProcessType || 'ожидание');
  const [queuePosition, setQueuePosition] = useState(0);
  const [error, setError] = useState(state.error || null);
  const [showTimestamps, setShowTimestamps] = useState(state.showTimestamps !== false);
  const [highlightSpeakers, setHighlightSpeakers] = useState(state.highlightSpeakers !== false);
  const audioRef = useRef(null);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [playingWordIndex, setPlayingWordIndex] = useState(null);
  const segmentRefs = useRef([]);
  const fileInputRef = useRef(null);

  const audioUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  useEffect(() => {
    const saved = getTasksForComponent('dual-channel');
    if (!saved.length) return;
    const task = saved[0];
    (async () => {
      try {
        const resp = await axios.get(task.statusEndpoint, { headers: authService.getAuthHeaders() });
        const { status } = resp.data;
        if (status === 'READY') {
          const result = await axios.get(task.resultEndpoint, { headers: authService.getAuthHeaders() });
          setSegments(normalizeSegments(result.data.segments));
          removeTask(task.taskId);
        } else if (status === 'ERROR') {
          setError('Задача завершилась с ошибкой');
          removeTask(task.taskId);
        } else {
          // CREATED / UPLOADING / PENDING / PROCESSING — возобновляем поллинг
          setTaskId(task.taskId);
          setCurrentProcessType('транскрибация');
          setIsProcessing(true);
        }
      } catch {
        removeTask(task.taskId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  useEffect(() => {
    setState({
      file,
      segments,
      taskId,
      isProcessing,
      progress,
      uploadProgress,
      transcribeProgress,
      currentProcessType,
      error,
      showTimestamps,
      highlightSpeakers
    });
  }, [file, segments, taskId, isProcessing, progress, uploadProgress, transcribeProgress,
      currentProcessType, error, showTimestamps, highlightSpeakers, setState]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      resetState();
    }
  };

  const handleDragOver = (event) => event.preventDefault();

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      resetState();
    }
  };

  const resetState = () => {
    setSegments([]);
    setTaskId(null);
    setIsProcessing(false);
    setProgress(0);
    setUploadProgress(0);
    setTranscribeProgress(0);
    setCurrentProcessType('ожидание');
    setQueuePosition(0);
    setCurrentSegment(null);
    setPlayingWordIndex(null);
    setError(null);
  };

  const handleCancel = async () => {
    logUiEvent('cancel', { component: 'dual-channel', task_id: taskId });
    await transcribeService.cancelTask(API_BASE_URL_DUAL, taskId);
    if (taskId) removeTask(taskId);
    setIsProcessing(false);
    resetState();
  };

  const handleUpload = async () => {
    if (!file) {
      fileInputRef.current.click();
      return;
    }

    resetState();
    setIsProcessing(true);
    setCurrentProcessType('загрузка файла');

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Загрузка живёт минуты и переживает access-токен, поэтому свой кред —
      // часовой токен назначения upload, а не Bearer (docs/superpowers/sdd/…).
      const uploadHeaders = await authService.getUploadHeaders();
      const response = await axios.post(`${API_BASE_URL_DUAL}/start_dual_channel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...uploadHeaders,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          setProgress(percentCompleted / 2); // Загрузка составляет 50% общего прогресса
        }
      });

      setTaskId(response.data.task_id);
      setCurrentProcessType('транскрибация');
      saveTask({
        taskId: response.data.task_id,
        service: 'bit-dual-channel-v3',
        statusEndpoint: `${API_BASE_URL_DUAL}/get_status?task_id=${response.data.task_id}`,
        resultEndpoint: `${API_BASE_URL_DUAL}/get_json_result_v3?task_id=${response.data.task_id}`,
        fileName: file?.name || '',
        startedAt: new Date().toISOString(),
        component: 'dual-channel',
      });
    } catch (err) {
      console.error('Error uploading file:', err);
      const blocked = await resolveQuotaBlockedError(err);
      setError(blocked
        ? `${blocked}. Продлить или сменить тариф — в личном кабинете, кнопка «Сменить тариф».`
        : 'Ошибка при загрузке файла: ' + (err.response?.data?.detail || err.message));
      setIsProcessing(false);
    }
  };

  const checkStatus = useCallback(async () => {
    if (!taskId || !isProcessing) return;

    try {
      const response = await axios.get(`${API_BASE_URL_DUAL}/get_status?task_id=${taskId}`, {
        headers: authService.getAuthHeaders()
      });

      const { status, progress: taskProgress, queue_position } = response.data;

      setQueuePosition(queue_position || 0);
      // Обновляем прогресс транскрибации (50-100%)
      setTranscribeProgress(taskProgress);
      setProgress(50 + (taskProgress / 2)); // Транскрибация составляет вторую половину общего прогресса

      if (status === 'READY') {
        setIsProcessing(false);
        removeTask(taskId);
        await fetchResult();
      } else if (status === 'ERROR') {
        setError('Ошибка при обработке файла: ' + (response.data.error || ''));
        setIsProcessing(false);
        removeTask(taskId);
      }
    } catch (err) {
      console.error('Error checking task status:', err);
      setError('Ошибка при проверке статуса: ' + (err.response?.data?.detail || err.message));
      setIsProcessing(false);
      if (taskId) removeTask(taskId);
    }
  }, [taskId, isProcessing]);

  useEffect(() => {
    let intervalId;
    if (isProcessing && taskId) {
      intervalId = setInterval(checkStatus, 2000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isProcessing, taskId, checkStatus]);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL_DUAL}/get_json_result_v3?task_id=${taskId}`, {
        headers: authService.getAuthHeaders()
      });

      setSegments(normalizeSegments(response.data.segments));
    } catch (err) {
      console.error('Error fetching result:', err);
      setError('Ошибка при получении результата: ' + (err.response?.data?.detail || err.message));
    }
  };

  const playSegment = async (segment, index) => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (!audio.paused) audio.pause();
      await new Promise(resolve => setTimeout(resolve, 50));
      audio.currentTime = segment.start_time;

      while (audio.readyState < 2) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      await audio.play();
      setCurrentSegment(segment);
      setPlayingWordIndex(null);

      if (segmentRefs.current[index]) {
        segmentRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (err) {
      console.error('Error playing audio:', err);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setCurrentSegment(null);
      setPlayingWordIndex(null);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!currentSegment || !audioRef.current || !Array.isArray(currentSegment.words)) return;

      const time = audioRef.current.currentTime;
      const wordIdx = currentSegment.words.findIndex(w =>
        time >= w.start_time && time <= w.end_time
      );

      setPlayingWordIndex(wordIdx);

      if (time >= currentSegment.end_time) {
        audioRef.current.pause();
        setCurrentSegment(null);
        setPlayingWordIndex(null);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [currentSegment]);

  const renderTextWithHighlight = (segment) => {
    if (!Array.isArray(segment.words) || !segment.words.length) return segment.text;

    return segment.words.map((w, i) => (
      <span
        key={i}
        className={i === playingWordIndex ? 'bg-yellow-300' : ''}
        title={`Начало: ${w.start_time.toFixed(2)}s`}
      >
        {(w.display || w.word) + ' '}
      </span>
    ));
  };

  const formatTimestamp = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const milliseconds = Math.floor((time % 1) * 1000);

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
  };

  const handleCopy = () => {
    if (segments.length > 0) {
      const text = segments.map(seg => {
        const speaker = stereoRoleLabel(seg.speaker_role);
        const timestamp = showTimestamps ?
          `[${formatTimestamp(seg.start_time)} - ${formatTimestamp(seg.end_time)}]` : '';
        return `${highlightSpeakers ? `${speaker} ` : ''}${timestamp}${timestamp ? ': ' : ''}${seg.text}`;
      }).join('\n\n');

      navigator.clipboard.writeText(text);
    }
  };

  const handleSave = () => {
    if (segments.length > 0) {
      const text = segments.map(seg => {
        const speaker = stereoRoleLabel(seg.speaker_role);
        const timestamp = showTimestamps ?
          `[${formatTimestamp(seg.start_time)} - ${formatTimestamp(seg.end_time)}]` : '';
        return `${highlightSpeakers ? `${speaker} ` : ''}${timestamp}${timestamp ? ': ' : ''}${seg.text}`;
      }).join('\n\n');

      const element = document.createElement("a");
      const blob = new Blob([text], {type: 'text/plain'});
      element.href = URL.createObjectURL(blob);
      element.download = buildDownloadName(file?.name, 'trnscr', 'txt');
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  const handleDelete = () => {
    logUiEvent('new', { component: 'dual-channel' });
    setFile(null);
    resetState();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4 font-montserrat">
      <div className="flex flex-col md:flex-row md:justify-between items-start mb-2 gap-4 md:gap-2">
        <div className="w-full md:w-auto space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="checkbox-timestamps"
              checked={showTimestamps}
              onChange={(e) => setShowTimestamps(e.target.checked)}
            />
            <label htmlFor="checkbox-timestamps" className="text-sm">
              Показывать метки времени
            </label>
            <Button
              id="help-timestamps"
              variant="ghost"
              size="sm"
              className="p-0 h-auto border-0 outline-0 ring-0"
              title="Включает/выключает отображение временных меток для каждой реплики"
            >
              <HelpCircle className="w-4 h-4 bg-paper-panel0 text-white rounded-full outline-none border-none ring-none" />
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="checkbox-speakers"
              checked={highlightSpeakers}
              onChange={(e) => setHighlightSpeakers(e.target.checked)}
            />
            <label htmlFor="checkbox-speakers" className="text-sm">
              Выделять участников
            </label>
            <Button
              id="help-speakers"
              variant="ghost"
              size="sm"
              className="p-0 h-auto"
              title="Показывает роль каждого участника (Менеджер/Клиент)"
            >
              <HelpCircle className="w-4 h-4 bg-paper-panel0 text-white rounded-full" />
            </Button>
          </div>
        </div>

        <div className="flex gap-2 w-full md:w-auto justify-start md:justify-end mt-1">
          <Button
            id="button-copy"
            onClick={handleCopy}
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-paper-bg hover:bg-paper-track "
            title="Копировать"
            disabled={segments.length === 0}
          >
            <Copy className="w-4 h-4 text-paper-ink-2" />
          </Button>

          <Button
            id="button-save"
            onClick={handleSave}
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-paper-bg hover:bg-paper-track "
            title="Скачать"
            disabled={segments.length === 0}
          >
            <Download className="w-4 h-4 text-paper-ink-2" />
          </Button>

          <Button
            id="button-delete"
            onClick={handleDelete}
            variant="outline"
            size="icon"
            className="h-8 w-8 bg-paper-bg hover:bg-paper-track "
            title="Удалить"
          >
            <Trash2 className="w-4 h-4 text-paper-ink-2" />
          </Button>
        </div>
      </div>

      <div
        id="dropzone"
        className="border-2 border-dashed rounded-panel p-6 cursor-pointer hover:bg-paper-panel transition-colors"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current.click()}
      >
        <div className="flex flex-col items-center">
          <Upload className="w-12 h-12 text-paper-hint mb-2" />
          <p className="text-center text-paper-ink-3">
            {file ? file.name : "Перетащите аудиофайл сюда или кликните для выбора"}
          </p>
          <input
            id="file-input"
            type="file"
            onChange={handleFileChange}
            className="hidden"
            ref={fileInputRef}
            accept="audio/*"
          />
        </div>
      </div>

      <Button
        id="button-start"
        onClick={handleUpload}
        className="w-full bg-paper-accent hover:bg-paper-accent-hover text-white h-12 font-montserrat"
        disabled={isProcessing}
      >
        {isProcessing ? 'Обработка...' : 'Начать транскрибацию'}
      </Button>

      {isProcessing && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <p id="process-type" className="text-sm text-paper-ink-3 flex-1">
              {queuePosition > 0
                ? `Место в очереди: ${queuePosition}`
                : currentProcessType}
            </p>
            <button
              onClick={handleCancel}
              className="text-paper-hint hover:text-paper-accent transition-colors text-base leading-none px-1"
              title="Отменить"
            >
              ✕
            </button>
          </div>
          {queuePosition === 0 && (
            <Progress
              id="progress-bar"
              value={
                currentProcessType === 'загрузка файла'
                  ? uploadProgress
                  : transcribeProgress
              }
              className="h-2 bg-paper-bg"
              indicatorClassName="bg-paper-accent"
            />
          )}
        </div>
      )}

      {error && (
        <div id="error-message" className="text-paper-accent text-sm mb-4">
          {error}
        </div>
      )}

      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="auto" className="hidden" />}

      {segments.length > 0 && (
        <div id="transcription-result" className="bg-paper-panel rounded-panel max-h-screen overflow-y-auto">
          <div className="sticky top-0 bg-paper-panel px-4 pt-4 pb-2 z-10">
            <h3 className="font-medium">Результат транскрибации:</h3>
          </div>

          <div className="px-4 pb-4 space-y-3">
            {segments.map((segment, idx) => (
              <div
                key={idx}
                ref={(el) => (segmentRefs.current[idx] = el)}
                className={`p-3 rounded-panel border ${
                  segment.speaker_role === 'manager' 
                    ? ' bg-paper-bg' 
                    : ' bg-paper-bg'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    {highlightSpeakers && (
                      <span className={`font-bold ${
                        segment.speaker_role === 'manager' 
                          ? 'text-paper-accent' 
                          : 'text-paper-ink-2'
                      }`}>
                        {stereoRoleLabel(segment.speaker_role)}
                      </span>
                    )}

                    {showTimestamps && (
                      <span className="text-paper-ink-3 text-sm ml-2">
                        [{formatTimestamp(segment.start_time)} - {formatTimestamp(segment.end_time)}]
                      </span>
                    )}
                  </div>

                  <div className="flex space-x-1">
                    <Button
                      onClick={() => playSegment(segment, idx)}
                      variant="outline"
                      size="icon"
                      className="h-6 w-6 rounded-full"
                    >
                      <PlayCircle className="h-4 w-4" />
                    </Button>

                    <Button
                      onClick={stopAudio}
                      variant="outline"
                      size="icon"
                      className="h-6 w-6 rounded-full"
                    >
                      <StopCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="text-sm">
                  {currentSegment?.start_time === segment.start_time
                    ? renderTextWithHighlight(segment)
                    : segment.text
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DualChannelTranscription;